/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const state = {
  branchServices: [],
  selectedBranchService: null,
  pagination: {
    page: 1,
    page_size: 10,
    total: 0,
    next: null,
  },
  filters: {
    branch_id: null,
    service_id: null,
    enabled: null,
  },
  isLoading: false,
  isError: false,
  allBranchServices: [],
  isAllBranchServicesLoading: false,
};

const getters = {
  getBranchServices: state => state.branchServices,
  getSelectedBranchService: state => state.selectedBranchService,
  getBranchServicePagination: state => state.pagination,
  formattedPagination: (state) => {
    const { page, total, next, page_size } = state.pagination;
    return {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(total / page_size)),
      nextPage: next,
      pageSize: page_size,
    };
  },
  getAllBranchServices: state => state.allBranchServices,
};

const mutations = {
  SET_BRANCH_SERVICES(state, data) { state.branchServices = data; },
  SET_SELECTED_BRANCH_SERVICE(state, data) { state.selectedBranchService = data; },
  SET_PAGINATION(state, pagination) { state.pagination = pagination; },
  SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
  SET_IS_LOADING(state, isLoading) { state.isLoading = isLoading; },
  SET_IS_ERROR(state, isError) { state.isError = isError; },
  SET_ALL_BRANCH_SERVICES(state, data) { state.allBranchServices = data; },
  SET_IS_ALL_BRANCH_SERVICES_LOADING(state, isLoading) { state.isAllBranchServicesLoading = isLoading; },
};

const actions = {
  async fetchAllBranchServices({ commit, rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_ALL_BRANCH_SERVICES_LOADING', true);

      const allResults = [];
      let page = 1;
      const pageSize = 100;
      let hasMore = true;

      const { branch_id, service_id, enabled } = state.filters;

      while (hasMore) {
        const res = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-services`),
          { params: { branch_id, service_id, enabled, page, page_size: pageSize } }
        );

        const results = res.data.results || [];
        allResults.push(...results);

        hasMore = results.length === pageSize;
        page++;
      }

      commit('SET_ALL_BRANCH_SERVICES', allResults);
      return allResults;

    } catch (error) {
      console.error('Fetch all branch services failed:', error);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_ALL_BRANCH_SERVICES_LOADING', false);
    }
  },

  async fetchBranchServices({ commit, rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_LOADING', true);
      commit('SET_IS_ERROR', false);

      const { branch_id, service_id, enabled } = state.filters;

      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-services`),
        {
          params: {
            branch_id,
            service_id,
            enabled,
            page: state.pagination.page,
            page_size: state.pagination.page_size,
          },
        }
      );

      const { results, page, total, next } = res.data;
      commit('SET_BRANCH_SERVICES', results);
      commit('SET_PAGINATION', { ...state.pagination, page, total, next });

      return res.data;
    } catch (error) {
      console.error('Fetch branch services failed:', error);
      commit('SET_IS_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  async createBranchService({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-services`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create branch service failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteBranchService({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-services`),
        { data: payload }
      );
      return res.data;
    } catch (error) {
      console.error('Delete branch service failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateBranchServiceStatus({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-services/status`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Update branch service status failed:', error);
      throw error.response?.data || error;
    }
  },

  async bulkAssignServicesToBranch({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-services/bulk`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Bulk assign services to branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async bulkAssignBranchesToService({ rootGetters }, { serviceId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/${serviceId}/branches/bulk`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Bulk assign branches to service failed:', error);
      throw error.response?.data || error;
    }
  },

  async updatePaginationAndFetch({ commit, dispatch, state }, newPagination) {
    try {
      commit('SET_PAGINATION', { ...state.pagination, ...newPagination });
      await dispatch('fetchBranchServices');
      return true;
    } catch (error) {
      console.error('Update pagination and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateFiltersAndFetch({ commit, dispatch, state }, filters = {}) {
    try {
      commit('SET_FILTERS', filters);
      commit('SET_PAGINATION', { ...state.pagination, page: 1 });
      await dispatch('fetchBranchServices');
      return true;
    } catch (error) {
      console.error('Update filters and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async clearFiltersAndFetch({ commit, dispatch }) {
    try {
      commit('SET_PAGINATION', { page: 1, page_size: 10, total: 0, next: null });
      commit('SET_FILTERS', { service_id: null, enabled: null });
      await dispatch('fetchBranchServices');
      return true;
    } catch (error) {
      console.error('Clear filters and fetch failed:', error);
      throw error.response?.data || error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
